<script setup lang="ts">
import { useApp } from "./useApp";

const {
  hideNavigation,
  isDark,
  isSidebarOpen,
  canSeeAssetSummary,
  canSeeInvoices,
  canSeeProjects,
  canSeeRemoteAccess,
  canSeeReports,
  canSeeTicketSummary,
  userDisplayName,
  userInitials,
  userEmail,
  setTheme,
  signOut,
  signOutLoading,
  unreadMessagesCount,
  unreadMessagesBadgeLabel,
  getAppIconLink,
  getOrganizationName,
  isDocumentsNavActive,
} = useApp();
</script>
<template>
  <div id="q-app" class="relative-position">
    <div class="lp-spinner-overlay sectionloader" :class="{ 'is-visible': signOutLoading }" aria-live="polite"
      :aria-busy="signOutLoading" :aria-hidden="!signOutLoading">
      <div class="lp-spinner"></div>
    </div>
    <q-layout view="lHh Lpr lFf" class="app">
      <!-- Mobile Header -->
      <q-header class="mobile-header row items-center justify-between q-pa-md"
        v-if="$q.screen.lt.lg && !hideNavigation">
        <img v-if="getAppIconLink" :key="`portal-logo-mobile-${isDark}`" :src="getAppIconLink"
          :alt="getOrganizationName || 'Logo'" class="logo-mobile" />
        <q-btn flat round dense aria-label="Menu" @click="isSidebarOpen = !isSidebarOpen">
          <Icon icon="menu01" :size="16" class="icon-header" />
        </q-btn>
      </q-header>

      <!-- Sidebar (q-drawer) -->
      <q-drawer v-if="!hideNavigation" v-model="isSidebarOpen" show-if-above :width="$q.screen.width < 1400 ? 215 : 250"
        :breakpoint="1024" :overlay="$q.screen.width < 1281" :behavior="$q.screen.width < 1281 ? 'mobile' : 'desktop'"
        class="sidebar" aria-label="Navigation Sidebar">
        <div>
          <div class="sidebar__brand row items-center">
            <img v-if="getAppIconLink" :key="`portal-logo-sidebar-${isDark}`" :src="getAppIconLink"
              :alt="getOrganizationName || 'Logo'" class="logo-sidebar" />
          </div>
          <div class="sidebar__section">
            <nav class="sidebar__nav-block column" aria-label="Primary">
              <router-link class="nav-link" to="/ticketAdd" active-class="is-active">
                <span class="nav-link__icon">
                  <Icon icon="addCircle" :size="16" />
                </span>
                New request
              </router-link>
              <router-link class="nav-link" to="/ticketList" active-class="is-active">
                <span class="nav-link__icon">
                  <Icon icon="chat01" :size="16" aria-hidden="true" />
                </span>
                <span class="nav-link__label">Messages</span>
                <span v-if="unreadMessagesCount > 0" class="nav-link__badge" :aria-label="`${unreadMessagesCount} unread message${unreadMessagesCount === 1 ? '' : 's'
                  }`">
                  {{ unreadMessagesBadgeLabel }}
                </span>
              </router-link>
              <router-link v-slot="{ href, navigate }" custom :to="{ name: 'DocumentManage' }">
                <a :href="href" class="nav-link" :class="{ 'is-active': isDocumentsNavActive }" @click="navigate">
                  <span class="nav-link__icon">
                    <Icon icon="folderLibrary" :size="16" aria-hidden="true" />
                  </span>
                  Documents
                </a>
              </router-link>
              <router-link class="nav-link" to="/invoiceList" active-class="is-active" v-if="canSeeInvoices">
                <span class="nav-link__icon">
                  <Icon icon="invoice03" :size="16" aria-hidden="true" />
                </span>
                Invoices
              </router-link>
              <router-link class="nav-link" to="/projectList" active-class="is-active" v-if="canSeeProjects">
                <span class="nav-link__icon">
                  <Icon icon="blockchain01" :size="16" aria-hidden="true" />
                </span>
                Projects
              </router-link>
              <router-link class="nav-link" to="/remoteAccess" active-class="is-active" v-if="canSeeRemoteAccess">
                <span class="nav-link__icon">
                  <Icon icon="remoteAccess" :size="16" :stroke-width="1.8" aria-hidden="true" />
                </span>
                Remote Access
              </router-link>
            </nav>
            <template v-if="canSeeReports">
              <div class="sidebar__sep" role="separator"></div>
              <div>
                <div class="sidebar__label">Reports</div>
                <nav class="sidebar__nav-block" aria-label="Reports">
                  <router-link class="nav-link" to="/assetSummary" active-class="is-active" v-if="canSeeAssetSummary">
                    <span class="nav-link__icon">
                      <Icon icon="laptop" :size="16" aria-hidden="true" />
                    </span>
                    Asset summary
                  </router-link>
                  <router-link class="nav-link" to="/ticketSummary" active-class="is-active" v-if="canSeeTicketSummary">
                    <span class="nav-link__icon">
                      <Icon icon="ticket01" :size="16" aria-hidden="true" />
                    </span>
                    Ticket summary
                  </router-link>
                </nav>
              </div>
            </template>
          </div>
        </div>
        <div class="sidebar__footer">
          <div class="theme-toggle">
            <q-btn flat no-caps class="theme-toggle__btn" :class="{ 'is-on': isDark }" @click="setTheme(true)">
              <Icon icon="moon02" :size="16" class="q-mr-xs" aria-hidden="true" />
              Dark
            </q-btn>
            <q-btn flat no-caps class="theme-toggle__btn" :class="{ 'is-on': !isDark }" @click="setTheme(false)">
              <Icon icon="sun01" :size="16" class="q-mr-xs" aria-hidden="true" />
              Light
            </q-btn>
          </div>

          <div class="user-row row items-center cursor-pointer">
            <q-btn flat dense :ripple="false" square padding="none" class="avatar-menu-trigger"
              aria-label="Account menu" aria-haspopup="true">
              <div class="avatar" aria-hidden="true">{{ userInitials }}</div>
            </q-btn>
            <span class="user-row__name">{{ userDisplayName }}</span>
            <q-menu anchor="top left" self="bottom left" :offset="[0, 8]" class="accountMenuArea" fit>
              <q-list dense>
                <q-item class="q-px-md q-pt-sm q-pb-xs">
                  <q-item-section avatar>
                    <Icon icon="mail01" :size="16" class="col-drag-icon col-drag-handle" aria-hidden="true" />
                  </q-item-section>
                  <q-item-section>
                    {{ userEmail }}
                  </q-item-section>
                </q-item>
                <q-item clickable v-close-popup @click="signOut">
                  <q-item-section avatar>
                    <Icon icon="logoutIcon02" :size="16" class="col-drag-icon col-drag-handle" aria-hidden="true" />
                  </q-item-section>
                  <q-item-section>Log out</q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </div>
        </div>
      </q-drawer>

      <q-page-container :class="!hideNavigation ? 'frame-wrap' : 'login-wrapper'">
        <router-view />
      </q-page-container>
    </q-layout>
  </div>
</template>

<style>
body.theme-dark {
  --color-brand-05: var(--color-brand-05-dark);
  --color-buttons-primary-surface: var(--color-buttons-primary-surface-dark);
  --color-buttons-primary-surface-hover: var(--color-buttons-primary-surface-hover-dark);
}

body.theme-light {
  --color-brand-05: var(--color-brand-05-light);
  --color-buttons-primary-surface: var(--color-buttons-primary-surface-light);
  --color-buttons-primary-surface-hover: var(--color-buttons-primary-surface-hover-light);
}
</style>
